import React, { useMemo, useState, useEffect } from 'react';
import { getClientChart } from '@/services/api/dashboard';

interface ClientData {
    clientName: string;
    activeEV: number;
}

interface ClientsByActiveEVChartProps {
    onSeeAll?: () => void;
    data?: ClientData[];
}

const ClientsByActiveEVChart: React.FC<ClientsByActiveEVChartProps> = ({ onSeeAll, data }) => {
    const [apiData, setApiData] = useState<ClientData[]>([]);
    const [loading, setLoading] = useState(false);
    const initialized = React.useRef(false);

    // Fetch data from API on mount
    useEffect(() => {
        if (initialized.current) return;
        initialized.current = true;

        const fetchChartData = async () => {
            try {
                setLoading(true);
                const response = await getClientChart();
                const responseData = response.data || [];

                // Map API response to ClientData format - take top 6
                const mappedData: ClientData[] = responseData.slice(0, 6).map((item: any) => ({
                    clientName: item.clientName || 'Unknown',
                    activeEV: Number(item.totalVehicle) || 0
                }));

                setApiData(mappedData);
            } catch (error) {
                console.error('Error fetching client chart data:', error);
                setApiData([]);
            } finally {
                setLoading(false);
            }
        };

        fetchChartData();
    }, []);

    // Use provided data prop, or API data, or empty array
    const clientData: ClientData[] = useMemo(() => data || apiData, [data, apiData]);

    // Varied solid colors (darker tones)
    const barColors = ['#1D4ED8', '#0E7490', '#6B21A8', '#D97706', '#047857', '#DC2626'];

    // Find max value for calculating percentages
    const maxValue = useMemo(() => Math.max(...clientData.map(d => d.activeEV)), [clientData]);

    return (
        <div className="bg-white rounded-2xl border border-gray-200 px-6 pt-5 pb-6 shadow-sm">
            {/* Header */}
            <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center">
                        <i className="ri-bar-chart-box-line text-lg text-neutral-900" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900" style={{ letterSpacing: '-0.011em', lineHeight: '24px' }}>
                        Clients by Active EV
                    </h3>
                </div>

                <button
                    onClick={onSeeAll}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                    See All
                </button>
            </div>

            {/* Custom Bar Chart */}
            <div className="mt-6 space-y-4">
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    </div>
                ) : (
                    clientData.map((client, index) => {
                        const percentage = (client.activeEV / maxValue) * 100;
                        const color = barColors[index % barColors.length];

                        return (
                            <div key={`${client.clientName}-${index}`} className="space-y-2">
                                {/* Label Row: Client Name (left) + Units (right) */}
                                <div className="flex items-center justify-between">
                                    <span
                                        className="client-name-label"
                                        style={{
                                            fontFamily: 'Plus Jakarta Sans',
                                            fontWeight: 400,
                                            fontSize: '14px',
                                            lineHeight: '20px',
                                            letterSpacing: '-0.084px',
                                            color: '#171717',
                                            // Remove outline/border for pixel-perfect look
                                            border: 'none',
                                            outline: 'none',
                                        }}
                                    >
                                        {client.clientName}
                                    </span>
                                    <span
                                        className="font-normal"
                                        style={{
                                            fontSize: '16px',
                                            lineHeight: '24px',
                                            letterSpacing: '-0.011em',
                                            color: '#171717'
                                        }}
                                    >
                                        {client.activeEV.toLocaleString()} Units
                                    </span>
                                </div>

                                {/* Bar */}
                                <div className="w-full bg-gray-100 rounded-md h-6 overflow-hidden">
                                    <div
                                        className="h-full rounded-md transition-all duration-500 ease-out"
                                        style={{
                                            width: `${percentage}%`,
                                            backgroundColor: color,
                                        }}
                                    />
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default ClientsByActiveEVChart;
